import { CopyLinkField } from '@/components/ui/clipboard/CopyLinkField';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import {
  DisplayRightsDeliveryChannel,
  type SendDisplayRightsProposalResponse,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { DisplayRightsStatusBadges } from './DisplayRightsStatusBadges';
import type { DisplayRightsMetaState } from './display-rights-status';

interface DisplayRightsSentPanelProps {
  result: SendDisplayRightsProposalResponse;
  /** Shown next to the contract pill so the crossed state stays readable. */
  metaState?: DisplayRightsMetaState;
  onDone?: () => void;
}

/**
 * What happened, once the proposal is out.
 *
 * The link is always offered, whatever the channel: the mailbox rail is
 * asynchronous, so "queued" is the honest word, and a delivery that fails later
 * must never leave the brand without a way to reach the creator. The crossed
 * state is shown here too, so the brand leaves knowing both dimensions.
 */
export function DisplayRightsSentPanel({ result, metaState, onDone }: DisplayRightsSentPanelProps) {
  const { t } = useTranslation();

  const emailed =
    result.delivery.channel === DisplayRightsDeliveryChannel.CONNECTED_EMAIL &&
    result.delivery.emailQueued;

  return (
    <VStack align="stretch" gap="4" data-testid="display-rights-sent-panel">
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="fg">
          {emailed ? t('displayRights.sent.emailTitle') : t('displayRights.sent.linkTitle')}
        </Text>
        <Text fontSize="xs" color="fg.muted" mt="1" lineHeight="tall">
          {emailed
            ? t('displayRights.sent.emailDescription')
            : t('displayRights.sent.linkDescription')}
        </Text>
      </Box>

      <DisplayRightsStatusBadges proposal={result.proposal} metaState={metaState} />

      <VStack align="stretch" gap="1.5">
        {emailed && (
          <Text fontSize="11.5px" color="fg.muted">
            {t('displayRights.sent.orShareLink')}
          </Text>
        )}
        <CopyLinkField value={result.publicUrl} copyLabel={t('displayRights.sent.copy')} />
      </VStack>

      {onDone && (
        <Button
          width="full"
          variant="outline"
          size="sm"
          onClick={onDone}
          data-testid="display-rights-done"
        >
          {t('displayRights.sent.done')}
        </Button>
      )}
    </VStack>
  );
}
